import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';

export interface BadgeData {
  id: string;
  name: string;
  icon: string;
  color: string;
  route?: string;
  completed?: boolean;
  onPress?: () => void;
}

interface BadgeProps {
  badge: BadgeData;
  onPress?: () => void;
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
  clickable?: boolean;
}

const Badge: React.FC<BadgeProps> = ({
  badge,
  onPress,
  size = 'medium',
  showLabel = true,
  clickable = true,
}) => {
  const { colors } = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const handlePress = () => {
    if (onPress) {
      onPress();
    } else if (badge.onPress) {
      badge.onPress();
    }
  };

  const isCompleted = badge.completed || false;

  const sizeStyles = {
    small: {
      badgeSize: Platform.OS === 'web' ? 40 : 44,
      iconSize: 18,
      checkmarkSize: 18,
      fontSize: 10,
      padding: Platform.OS === 'web' ? 8 : 10,
      borderRadius: Platform.OS === 'web' ? 12 : 14,
    },
    medium: {
      badgeSize: Platform.OS === 'web' ? 48 : 52,
      iconSize: 24,
      checkmarkSize: 16,
      fontSize: Platform.OS === 'web' ? 11 : 12,
      padding: Platform.OS === 'web' ? 12 : 14,
      borderRadius: Platform.OS === 'web' ? 16 : 14,
    },
    large: {
      badgeSize: Platform.OS === 'web' ? 64 : 68,
      iconSize: 32,
      checkmarkSize: 20,
      fontSize: Platform.OS === 'web' ? 13 : 14,
      padding: Platform.OS === 'web' ? 16 : 18,
      borderRadius: Platform.OS === 'web' ? 20 : 18,
    },
  };

  const currentSize = sizeStyles[size];
  const showHover = clickable && isHovered;

  const badgeInner = (
    <View
      style={[
        styles.achievementBadge,
        {
          backgroundColor: showHover ? badge.color + '25' : badge.color + '15',
          transform: showHover ? [{ scale: 1.1 }] : [{ scale: 1 }],
          shadowOpacity: showHover ? 0.3 : 0.15,
          shadowRadius: showHover ? 12 : 8,
          elevation: showHover ? 6 : 4,
          minHeight: currentSize.badgeSize + currentSize.padding * 2,
          paddingVertical: currentSize.padding,
          paddingHorizontal: currentSize.padding,
          borderRadius: currentSize.borderRadius,
        },
      ]}
    >
      <View style={styles.badgeContent}>
        <View
          style={[
            styles.badgeIcon,
            {
              backgroundColor: badge.color,
              width: currentSize.badgeSize,
              height: currentSize.badgeSize,
              borderRadius: currentSize.badgeSize / 2,
              transform: showHover ? [{ scale: 1.1 }] : [{ scale: 1 }],
            },
          ]}
        >
          <Ionicons
            name={badge.icon as React.ComponentProps<typeof Ionicons>['name']}
            size={currentSize.iconSize}
            color="white"
          />
        </View>
        {isCompleted && (
          <View style={styles.checkmarkContainer}>
            <View
              style={[
                styles.checkmarkCircle,
                {
                  width: currentSize.checkmarkSize + 8,
                  height: currentSize.checkmarkSize + 8,
                  borderRadius: (currentSize.checkmarkSize + 8) / 2,
                },
              ]}
            >
              <Ionicons
                name="checkmark"
                size={currentSize.checkmarkSize}
                color="white"
              />
            </View>
          </View>
        )}
      </View>
      {showLabel && (
        <Text
          style={[
            styles.badgeLabel,
            {
              color: colors.text,
              fontSize: currentSize.fontSize,
              marginTop: Platform.OS === 'web' ? 8 : 10,
            },
          ]}
        >
          {badge.name}
        </Text>
      )}
    </View>
  );

  return (
    <View
      style={styles.badgeWrapper}
      {...(Platform.OS === 'web' && clickable
        ? {
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
          }
        : {})}
    >
      {clickable ? (
        <TouchableOpacity onPress={handlePress} activeOpacity={0.8}>
          {badgeInner}
        </TouchableOpacity>
      ) : (
        badgeInner
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  badgeWrapper: {
    position: 'relative',
    alignItems: 'center',
    flex: Platform.OS === 'web' ? 0 : 1,
    minWidth: Platform.OS === 'web' ? 80 : 0,
    maxWidth: Platform.OS === 'web' ? 80 : '48%',
  },
  achievementBadge: {
    width: Platform.OS === 'web' ? 80 : '100%',
    alignItems: 'center',
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
    position: 'relative',
    transition: 'all 0.3s ease',
  },
  badgeContent: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.25,
    shadowRadius: 6,
    elevation: 5,
  },
  checkmarkContainer: {
    position: 'absolute',
    bottom: -4,
    right: -4,
    zIndex: 10,
  },
  checkmarkCircle: {
    backgroundColor: '#10B981',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: 'white',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 5,
  },
  badgeLabel: {
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default Badge;
